import { useState } from "react";
// THE LEGAL NAME: "Henry-Griffitts, LLC" (Idaho register, file 4043729). This agreement names the company as a party, so it
// uses the legal name; "Henry-Griffitts" elsewhere on this page is the brand. Backend owner: backend/company.py.
const LEGAL_NAME = "Henry-Griffitts, LLC";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", 
  "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", 
  "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", 
  "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export function OnboardingFlow({ onComplete, onCancel }) {
  const [step, setStep] = useState(1); // 1: Account Info, 2: License Agreement, 3: Confirmation
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  
  // Account Info Form State
  const [accountInfo, setAccountInfo] = useState({
    account_name: "",
    contact_name: "",
    cell_phone: "",
    email: "",
    physical_address: { street: "", city: "", state: "", zip_code: "", country: "USA" },
    mailing_address: { street: "", city: "", state: "", zip_code: "", country: "USA" },
    shipping_address: { street: "", city: "", state: "", zip_code: "", country: "USA" },
    billing_address: { street: "", city: "", state: "", zip_code: "", country: "USA" },
    telephone: "",
    fax: "",
    range_number: "",
    billing_contact_name: "",
    billing_telephone: "",
    billing_fax: "",
    can_accept_referrals: true,
    fitters_name: "",
    credit_card_consent: false,
    signature: "",
    signature_date: new Date().toISOString().split('T')[0]
  });

  // License Agreement Form State
  const [licenseAgreement, setLicenseAgreement] = useState({
    licensee_name: "",
    licensee_address: "",
    course_or_club_name: "",
    telephone: "",
    authorized_fitters: "",
    email: "",
    website: "",
    agreement_date: new Date().toISOString().split('T')[0],
    business_type: "",
    received_demo_kit: false,
    received_fitters_manual: false,
    received_license_agreement: false,
    received_code_of_practice: false,
    acknowledge_confidentiality: false,
    acknowledge_trademarks: false,
    acknowledge_termination: false,
    acknowledge_code_of_practice: false,
    licensee_signature: "",
    licensee_title: "",
    signature_date: new Date().toISOString().split('T')[0]
  });

  const [useSameAddress, setUseSameAddress] = useState({
    mailing: true,
    shipping: true,
    billing: true
  });

  function updateAccountField(field, value) {
    setAccountInfo(prev => ({ ...prev, [field]: value }));
  }

  function updateAddress(type, field, value) {
    setAccountInfo(prev => ({
      ...prev,
      [`${type}_address`]: {
        ...prev[`${type}_address`],
        [field]: value
      }
    }));
  }

  function updateLicenseField(field, value) {
    setLicenseAgreement(prev => ({ ...prev, [field]: value }));
  }

  function copyPhysicalAddress(targetType) {
    setAccountInfo(prev => ({
      ...prev,
      [`${targetType}_address`]: { ...prev.physical_address }
    }));
  }

  function validateStep1() {
    if (!accountInfo.account_name.trim()) return "Account name is required";
    if (!accountInfo.contact_name.trim()) return "Contact name is required";
    if (!accountInfo.cell_phone.trim()) return "Cell phone is required";
    if (!accountInfo.email.trim()) return "Email is required";
    if (!accountInfo.physical_address.street.trim()) return "Physical address is required";
    if (!accountInfo.physical_address.city.trim()) return "City is required";
    if (!accountInfo.physical_address.state.trim()) return "State is required";
    if (!accountInfo.physical_address.zip_code.trim()) return "ZIP code is required";
    if (!accountInfo.credit_card_consent) return "You must consent to the credit card authorization";
    if (!accountInfo.signature.trim()) return "Signature is required";
    return null;
  }

  function validateStep2() {
    if (!licenseAgreement.licensee_name.trim()) return "Licensee name is required";
    if (!licenseAgreement.course_or_club_name.trim()) return "Course/Club name is required";
    if (!licenseAgreement.authorized_fitters.trim()) return "Authorized fitters name is required";
    if (!licenseAgreement.acknowledge_confidentiality) return "You must acknowledge the confidentiality terms";
    if (!licenseAgreement.acknowledge_trademarks) return "You must acknowledge the trademark terms";
    if (!licenseAgreement.acknowledge_code_of_practice) return "You must acknowledge the Code of Practice";
    if (!licenseAgreement.licensee_signature.trim()) return "Signature is required";
    return null;
  }

  async function handleSubmit() {
    setError("");
    setSubmitting(true);

    // Sync addresses if using same
    const finalAccountInfo = { ...accountInfo };
    if (useSameAddress.mailing) finalAccountInfo.mailing_address = { ...accountInfo.physical_address };
    if (useSameAddress.shipping) finalAccountInfo.shipping_address = { ...accountInfo.physical_address };
    if (useSameAddress.billing) finalAccountInfo.billing_address = { ...accountInfo.physical_address };

    // Sync email and info to license agreement
    const finalLicenseAgreement = {
      ...licenseAgreement,
      email: accountInfo.email,
      telephone: accountInfo.telephone || accountInfo.cell_phone,
      licensee_address: `${finalAccountInfo.physical_address.street}, ${finalAccountInfo.physical_address.city}, ${finalAccountInfo.physical_address.state} ${finalAccountInfo.physical_address.zip_code}`
    };

    try {
      const res = await fetch(`${API_URL}/api/onboarding/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          account_info: finalAccountInfo,
          license_agreement: finalLicenseAgreement
        })
      });

      const data = await res.json();
      
      if (res.ok) {
        setStep(3);
      } else {
        setError(data.detail || "Failed to submit application");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    }
    setSubmitting(false);
  }

  function nextStep() {
    setError("");
    if (step === 1) {
      const err = validateStep1();
      if (err) {
        setError(err);
        return;
      }
      // Pre-fill license agreement with account info
      setLicenseAgreement(prev => ({
        ...prev,
        licensee_name: accountInfo.account_name,
        authorized_fitters: accountInfo.fitters_name || accountInfo.contact_name,
        email: accountInfo.email,
        telephone: accountInfo.telephone || accountInfo.cell_phone
      }));
      setStep(2);
    } else if (step === 2) {
      const err = validateStep2();
      if (err) {
        setError(err);
        return;
      }
      handleSubmit();
    }
  }

  return (
    <div className="onboarding-flow" data-testid="onboarding-flow">
      <div className="onboarding-header">
        <img 
          src="https://henry-griffitts.com/wp-content/uploads/2020/10/HG-tree-logo.png" 
          alt="Henry-Griffitts"
          className="onboarding-logo"
        />
        <h1>Become a Certified Fitter</h1>
        <p>Complete the forms below to apply for your Henry-Griffitts license.</p>
      </div>

      {/* Progress Steps */}
      <div className="onboarding-progress">
        <div className={`progress-step ${step >= 1 ? "active" : ""} ${step > 1 ? "completed" : ""}`}>
          <span className="step-num">1</span>
          <span className="step-label">Account Info</span>
        </div>
        <div className="progress-line" />
        <div className={`progress-step ${step >= 2 ? "active" : ""} ${step > 2 ? "completed" : ""}`}>
          <span className="step-num">2</span>
          <span className="step-label">License Agreement</span>
        </div>
        <div className="progress-line" />
        <div className={`progress-step ${step >= 3 ? "active" : ""}`}>
          <span className="step-num">3</span>
          <span className="step-label">Confirmation</span>
        </div>
      </div>

      {error && <div className="onboarding-error">{error}</div>}

      {/* Step 1: Account Information */}
      {step === 1 && (
        <div className="onboarding-form" data-testid="account-info-form">
          <h2>Account Information</h2>
          
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>New Account Name *</label>
                <input
                  type="text"
                  value={accountInfo.account_name}
                  onChange={(e) => updateAccountField("account_name", e.target.value)}
                  placeholder="Your business or facility name"
                  required
                />
              </div>
              <div className="form-field">
                <label>Contact Name *</label>
                <input
                  type="text"
                  value={accountInfo.contact_name}
                  onChange={(e) => updateAccountField("contact_name", e.target.value)}
                  placeholder="Primary contact person"
                  required
                />
              </div>
              <div className="form-field">
                <label>Cell Phone *</label>
                <input
                  type="tel"
                  value={accountInfo.cell_phone}
                  onChange={(e) => updateAccountField("cell_phone", e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
              <div className="form-field">
                <label>Email *</label>
                <input
                  type="email"
                  value={accountInfo.email}
                  onChange={(e) => updateAccountField("email", e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Physical Address *</h3>
            <div className="form-grid">
              <div className="form-field full-width">
                <label>Street Address</label>
                <input
                  type="text"
                  value={accountInfo.physical_address.street}
                  onChange={(e) => updateAddress("physical", "street", e.target.value)}
                  required
                />
              </div>
              <div className="form-field">
                <label>City</label>
                <input
                  type="text"
                  value={accountInfo.physical_address.city}
                  onChange={(e) => updateAddress("physical", "city", e.target.value)}
                  required
                />
              </div>
              <div className="form-field">
                <label>State</label>
                <select
                  value={accountInfo.physical_address.state}
                  onChange={(e) => updateAddress("physical", "state", e.target.value)}
                  required
                >
                  <option value="">Select State</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>ZIP Code</label>
                <input
                  type="text"
                  value={accountInfo.physical_address.zip_code}
                  onChange={(e) => updateAddress("physical", "zip_code", e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          {/* Same address checkboxes */}
          <div className="address-options">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={useSameAddress.mailing}
                onChange={(e) => setUseSameAddress({...useSameAddress, mailing: e.target.checked})}
              />
              Mailing address same as physical
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={useSameAddress.shipping}
                onChange={(e) => setUseSameAddress({...useSameAddress, shipping: e.target.checked})}
              />
              Shipping address same as physical
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={useSameAddress.billing}
                onChange={(e) => setUseSameAddress({...useSameAddress, billing: e.target.checked})}
              />
              Billing address same as physical
            </label>
          </div>

          <div className="form-section">
            <h3>Additional Information</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>Telephone</label>
                <input
                  type="tel"
                  value={accountInfo.telephone}
                  onChange={(e) => updateAccountField("telephone", e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Fax</label>
                <input
                  type="tel"
                  value={accountInfo.fax}
                  onChange={(e) => updateAccountField("fax", e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Fitter's Name at Location</label>
                <input
                  type="text"
                  value={accountInfo.fitters_name}
                  onChange={(e) => updateAccountField("fitters_name", e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Can you accept outside referrals?</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      checked={accountInfo.can_accept_referrals === true}
                      onChange={() => updateAccountField("can_accept_referrals", true)}
                    />
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      checked={accountInfo.can_accept_referrals === false}
                      onChange={() => updateAccountField("can_accept_referrals", false)}
                    />
                    No
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="form-section consent-section">
            <h3>Credit Card Authorization</h3>
            <p className="legal-text">
              I consent to let Henry-Griffitts charge my credit card at the time of order for products 
              and services rendered. This includes the monthly license fee of $57.50 and any equipment 
              or club orders placed through my account.
            </p>
            <label className="checkbox-label consent-checkbox">
              <input
                type="checkbox"
                checked={accountInfo.credit_card_consent}
                onChange={(e) => updateAccountField("credit_card_consent", e.target.checked)}
                required
              />
              I consent to the credit card authorization above *
            </label>
            
            <div className="form-field signature-field">
              <label>Signature (Type your full name) *</label>
              <input
                type="text"
                value={accountInfo.signature}
                onChange={(e) => updateAccountField("signature", e.target.value)}
                placeholder="Type your full legal name"
                className="signature-input"
                required
              />
              <span className="signature-date">Date: {accountInfo.signature_date}</span>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: License Agreement */}
      {step === 2 && (
        <div className="onboarding-form" data-testid="license-agreement-form">
          <h2>License Agreement</h2>
          <p className="legal-note">
            This License Agreement is between {LEGAL_NAME} and the Licensee identified below.
          </p>

          <div className="form-section">
            <h3>Licensee Information</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>Licensee Name *</label>
                <input
                  type="text"
                  value={licenseAgreement.licensee_name}
                  onChange={(e) => updateLicenseField("licensee_name", e.target.value)}
                  required
                />
              </div>
              <div className="form-field">
                <label>Course or Club Name *</label>
                <input
                  type="text"
                  value={licenseAgreement.course_or_club_name}
                  onChange={(e) => updateLicenseField("course_or_club_name", e.target.value)}
                  required
                />
              </div>
              <div className="form-field">
                <label>Authorized Fitters *</label>
                <input
                  type="text"
                  value={licenseAgreement.authorized_fitters}
                  onChange={(e) => updateLicenseField("authorized_fitters", e.target.value)}
                  required
                />
              </div>
              <div className="form-field">
                <label>Website</label>
                <input
                  type="url"
                  value={licenseAgreement.website}
                  onChange={(e) => updateLicenseField("website", e.target.value)}
                  placeholder="https://yourwebsite.com"
                />
              </div>
              <div className="form-field">
                <label>Business Type</label>
                <select
                  value={licenseAgreement.business_type}
                  onChange={(e) => updateLicenseField("business_type", e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="corp">Corporation</option>
                  <option value="llc">LLC</option>
                  <option value="sole_prop">Sole Proprietorship</option>
                  <option value="partnership">Partnership</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Licensed Products Received</h3>
            <p className="section-note">Confirm receipt of the following items:</p>
            <div className="checkbox-grid">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={licenseAgreement.received_demo_kit}
                  onChange={(e) => updateLicenseField("received_demo_kit", e.target.checked)}
                />
                Demo Kit (Schedule 1)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={licenseAgreement.received_fitters_manual}
                  onChange={(e) => updateLicenseField("received_fitters_manual", e.target.checked)}
                />
                Fitter's Manual
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={licenseAgreement.received_license_agreement}
                  onChange={(e) => updateLicenseField("received_license_agreement", e.target.checked)}
                />
                License Agreement
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={licenseAgreement.received_code_of_practice}
                  onChange={(e) => updateLicenseField("received_code_of_practice", e.target.checked)}
                />
                Code of Practice (Schedule 3)
              </label>
            </div>
          </div>

          <div className="form-section">
            <h3>Agreement Acknowledgments *</h3>
            <p className="section-note">Please read and acknowledge the following terms:</p>
            
            <div className="acknowledgment-item">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={licenseAgreement.acknowledge_confidentiality}
                  onChange={(e) => updateLicenseField("acknowledge_confidentiality", e.target.checked)}
                  required
                />
                <span>
                  <strong>Confidentiality:</strong> I acknowledge that Henry-Griffitts' Licensed Products 
                  are proprietary and agree to maintain confidentiality of all trade secrets, know-how, 
                  and proprietary information.
                </span>
              </label>
            </div>

            <div className="acknowledgment-item">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={licenseAgreement.acknowledge_trademarks}
                  onChange={(e) => updateLicenseField("acknowledge_trademarks", e.target.checked)}
                  required
                />
                <span>
                  <strong>Trademarks:</strong> I understand that all Henry-Griffitts trademarks, 
                  copyrights, and intellectual property remain the exclusive property of {LEGAL_NAME}.
                </span>
              </label>
            </div>

            <div className="acknowledgment-item">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={licenseAgreement.acknowledge_termination}
                  onChange={(e) => updateLicenseField("acknowledge_termination", e.target.checked)}
                />
                <span>
                  <strong>Termination:</strong> I understand the termination provisions of this agreement, 
                  including the requirement to return all licensed products upon termination.
                </span>
              </label>
            </div>

            <div className="acknowledgment-item">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={licenseAgreement.acknowledge_code_of_practice}
                  onChange={(e) => updateLicenseField("acknowledge_code_of_practice", e.target.checked)}
                  required
                />
                <span>
                  <strong>Code of Practice:</strong> I agree to abide by the Henry-Griffitts Code of Practice, 
                  including maintaining excellence standards, using approved promotional methods, and conducting 
                  all fittings at approved ranges or courses.
                </span>
              </label>
            </div>
          </div>

          <div className="form-section consent-section">
            <h3>Signature</h3>
            <p className="legal-text">
              By signing below, I agree to all terms and conditions of the Henry-Griffitts License Agreement.
            </p>
            
            <div className="form-grid">
              <div className="form-field signature-field">
                <label>Licensee Signature (Type your full name) *</label>
                <input
                  type="text"
                  value={licenseAgreement.licensee_signature}
                  onChange={(e) => updateLicenseField("licensee_signature", e.target.value)}
                  placeholder="Type your full legal name"
                  className="signature-input"
                  required
                />
              </div>
              <div className="form-field">
                <label>Title</label>
                <input
                  type="text"
                  value={licenseAgreement.licensee_title}
                  onChange={(e) => updateLicenseField("licensee_title", e.target.value)}
                  placeholder="e.g., Owner, Head Pro"
                />
              </div>
            </div>
            <span className="signature-date">Date: {licenseAgreement.signature_date}</span>
          </div>
        </div>
      )}

      {/* Step 3: Confirmation */}
      {step === 3 && (
        <div className="onboarding-confirmation" data-testid="onboarding-confirmation">
          <div className="success-icon">✓</div>
          <h2>Application Submitted!</h2>
          <p>
            Thank you for applying to become a Henry-Griffitts Certified Fitter.
            Your application has been submitted for review.
          </p>
          <div className="confirmation-details">
            <p><strong>What happens next?</strong></p>
            <ol>
              <li>Our team will review your application (typically 1-2 business days)</li>
              <li>You'll receive an email once your application is approved</li>
              <li>Create your account and begin your certification training</li>
            </ol>
          </div>
          <p className="confirmation-email">
            A confirmation has been sent to: <strong>{accountInfo.email}</strong>
          </p>
          <button className="btn btn-primary" onClick={onCancel}>
            Return to Home
          </button>
        </div>
      )}

      {/* Navigation Buttons */}
      {step < 3 && (
        <div className="onboarding-actions">
          {step > 1 && (
            <button 
              className="btn btn-secondary"
              onClick={() => setStep(step - 1)}
              disabled={submitting}
            >
              ← Previous
            </button>
          )}
          <button 
            className="btn btn-text"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={nextStep}
            disabled={submitting}
            data-testid="next-step-btn"
          >
            {submitting ? "Submitting..." : step === 2 ? "Submit Application" : "Continue →"}
          </button>
        </div>
      )}
    </div>
  );
}

export default OnboardingFlow;
